<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { PlanoPeriodicidade } from '../../../enums/PlanoPeriodicidade'
import { usePlanoStore } from './store/usePlanoStore'
import type { IPlano } from './types'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'
import { PlanoTipo } from '@/enums/PlanoTipo'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import LayoutTable from '@/components/CDF/LayoutTable.vue'
import type { IUser } from '@/pages/admin/users/types'

withDefaults(defineProps<{
  isHeader?: boolean
}>(), {
  isHeader: true,
})

definePage({
  meta: {
    action: 'list',
    subject: 'plano',
  },
})

const store = usePlanoStore()

const auth = useCookie<IUser>('userData').value
const isAdmin = computed(() => auth?.role?.slug === 'admin' || auth?.role?.slug === 'admin-system')

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  dialogDestroy,
  toggleAtivo,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: 'Título',
      key: 'titulo',
    },
    {
      title: 'Periodicidade',
      key: 'periodicidade',
    },
    {
      title: 'Tipo',
      key: 'tipo',
    },
    {
      title: 'Valor',
      key: 'valor',
    },
    {
      title: 'Ativo',
      key: 'ativo',
    },
  ]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: 'Título',
      value: 'titulo',
    },
    {
      title: 'Periodicidade',
      value: 'periodicidade',
    },
    {
      title: 'Tipo',
      value: 'tipo',
    },
    {
      title: 'Valor',
      value: 'valor',
    },
  ]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'plano',
    },
    to: (item: IPlano) => `/admin/plano/editar/${item.id}`,
  },
  ...(isAdmin.value ? [{
    icon: (item: IPlano) => item.ativo ? 'tabler-eye-off' : 'tabler-eye',
    color: (item: IPlano) => item.ativo ? 'warning' : 'success',
    tooltip: (item: IPlano) => item.ativo ? 'Inativar' : 'Ativar',
    can: {
      action: 'edit',
      subject: 'plano',
    },
    onClick: (item: IPlano) => toggleAtivo(item.id),
  }] : []),
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'plano',
    },
    onClick: (item: IPlano) => dialogDestroy(item.id),
  },
]

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Planos"

    new-item="/admin/plano/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #item.tipo="{ item }">
      {{ PlanoTipo[item.tipo] || 'N/A' }}
    </template>
    <template #item.periodicidade="{ item }">
      {{ PlanoPeriodicidade[item.periodicidade] || 'N/A' }}
    </template>
    <template #item.valor="{ item }">
      {{ formatCurrency(item.valor) }}
    </template>
    <template #item.ativo="{ item }">
      <VIcon :color="item.ativo ? 'green' : 'red'">
        {{ item.ativo ? 'tabler-check' : 'tabler-x' }}
      </VIcon>
    </template>
  </LayoutTable>
</template>
